import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear field error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const result = await register(formData);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      // Set up error object properly
      let newErrors = result.errors || {};
      if (result.message) {
        newErrors.general = result.message;
      }
      
      setErrors(newErrors);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 p-3 sm:p-5">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 sm:p-10 w-full max-w-md transform transition-all duration-500 hover:scale-105">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-gray-900 dark:text-white text-2xl sm:text-3xl font-bold mb-2">Create Account</h2>
          <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">Sign up for a new account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300 text-center">
              {errors.general}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-primary-100 dark:focus:ring-primary-500/20 ${
                errors.name 
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-500 text-gray-900 dark:text-white' 
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-white'
              }`}
              required
            />
            {errors.name && (
              <span className="text-red-500 dark:text-red-400 text-sm font-medium">{errors.name[0]}</span>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-primary-100 dark:focus:ring-primary-500/20 ${
                errors.email 
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-500 text-gray-900 dark:text-white' 
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-white'
              }`}
              required
            />
            {errors.email && (
              <span className="text-red-500 dark:text-red-400 text-sm font-medium">{errors.email[0]}</span>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-primary-100 dark:focus:ring-primary-500/20 ${
                errors.password 
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-500 text-gray-900 dark:text-white' 
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-white'
              }`}
              required
              minLength="8"
            />
            {errors.password && (
              <span className="text-red-500 dark:text-red-400 text-sm font-medium">{errors.password[0]}</span>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="password_confirmation" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Confirm Password
            </label>
            <input
              type="password"
              id="password_confirmation"
              name="password_confirmation"
              value={formData.password_confirmation}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-primary-100 dark:focus:ring-primary-500/20 ${
                errors.password_confirmation 
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-500 text-gray-900 dark:text-white' 
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-white'
              }`}
              required
              minLength="8"
            />
            {errors.password_confirmation && (
              <span className="text-red-500 dark:text-red-400 text-sm font-medium">{errors.password_confirmation[0]}</span>
            )}
          </div>

          <button 
            type="submit" 
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg py-4 text-base font-semibold cursor-pointer transition-all duration-300 hover:transform hover:scale-105 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none mt-4"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className="text-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-500 dark:text-primary-400 font-semibold hover:text-secondary-500 dark:hover:text-secondary-400 transition-colors duration-300">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;